import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import imageCompression from "browser-image-compression";
import { isMobile } from "react-device-detect";
import { Box, FormHelperText, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { colors } from "../utils/color";
import { getS3SignnedUrl } from "../store/api";
import axios from "axios";
import AppLoader from "./AppLoader";
import store from "../store";
import { SET_IMAGE } from "../store/actions/actionTypes";
import { useSelector } from "react-redux";
import { imagesSelector } from "../utils/selectorHelpers";

const ImageUploader = (props) => {
  const { name, value } = props;
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const images = useSelector(imagesSelector);
  const [preview, setPreview] = useState(images?.[name] || null);
  const { setFieldValue } = useFormikContext();
  const { t } = useTranslation();
  const maxFileSize = 1048576; // 1MB in bytes
  const minSize = 200;
  const maxBase64Size = 1_048_576;

  const compressImage = async (file) => {
    let quality = 0.9; // Start with high quality
    let compressedFile = file;
    while (compressedFile.size > maxFileSize && quality > 0.1) {
      const options = { maxSizeMB: quality, useWebWorker: true };
      compressedFile = await imageCompression(compressedFile, options);
      quality -= 0.1; // Reduce quality step by step
    }

    if (compressedFile.size > maxFileSize) {
      throw new Error("Unable to compress image under 1MB.");
    }

    return compressedFile;
  };
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const checkImageSize = (base64String) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64String;
      img.onload = () => {
        if (img.width < minSize || img.height < minSize) {
          reject(new Error("Image must be at least 200 x 200 pixels."));
        } else {
          resolve(base64String);
        }
      };
      img.onerror = () => reject(new Error("Failed to load image."));
    });
  };

  const processFile = async (file) => {
    setError("");
    setLoading(true);
    try {
      const finalCompressedFile = await compressImage(file);
      const base64String = await convertToBase64(finalCompressedFile)
      await checkImageSize(base64String);
      const finalBase64 = await compressBase64(base64String);
      const fileUrl = URL.createObjectURL(file);
      setPreview(fileUrl);
      const [header, data] = finalBase64.split(',');
      const contentType = header.match(/data:(.*);base64/)[1];
      const binaryData = atob(data); // decode base64

      // // Convert binary string to Uint8Array (needed for axios to upload properly)
      const byteArray = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        byteArray[i] = binaryData.charCodeAt(i);
      }
      const presignedUrl = await getS3SignnedUrl(contentType);

      let fileKeyValue = '';
      if (presignedUrl?.result) {
        const { fileKey, uploadUrl } = presignedUrl.result;
        const obj = {[name]: fileUrl};
        store.dispatch({
          type: SET_IMAGE,
          payload: obj
        })
        await axios.put(uploadUrl, byteArray, {
          headers: {
            'Content-Type': contentType,
          },
        });
        fileKeyValue = fileKey;
      } else {
        throw new Error('Failed to upload image.');
      }

      setFieldValue(name, fileKeyValue);
      setLoading(false);

    } catch (err) {
      setLoading(false);

      setError(err.message || "Failed to process image. Please upload a smaller file.");
      console.error(err);
    }
  };

  const compressBase64 = (base64String) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64String;
      img.onload = () => {
        if (img.width < minSize || img.height < minSize) {
          reject(new Error("Image must be at least 200 x 200 pixels."));
          return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, 0, 0, img.width, img.height);

        let quality = 0.9;
        let base64Compressed = canvas.toDataURL("image/jpeg", quality);

        while (base64Compressed.length > maxBase64Size && quality > 0.1) {
          quality -= 0.1;
          base64Compressed = canvas.toDataURL("image/jpeg", quality);
        }

        if (base64Compressed.length > maxBase64Size) {
          reject(new Error("Base64 output is too large even after compression."));
        } else {
          resolve(base64Compressed);
        }
      };
      img.onerror = () => reject(new Error("Failed to load image."));
    });
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processFile(acceptedFiles[0]);
    }
  };
  const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);
  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    maxSize: 6 * 1024 * 1024, // 5MB limit
    onDrop,
    onError: (err) => setError(err.message),
    onDropRejected: (reject) => {
      if (reject?.[0]?.file?.size) {
        const fileSizeMB = bytesToMB(reject[0].file.size);
        setError(`File is too large (${fileSizeMB} MB). Maximum allowed size is 5 MB.`);
      } else {
        setError(reject?.[0]?.errors?.[0]?.message || "File rejected.");
      }
    }
  });

  return (
    <Box>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <div
          {...getRootProps()}

          style={{
            border: "1px dashed #ddd",
            padding: 10,
            textAlign: "center",
            borderRadius: 2,
            width: '100%',
            backgroundColor: "rgba(0, 136, 245, 0.15)",
            cursor: 'pointer'
          }}
        >
          <input required={value ? false : props.required}  {...getInputProps()} name={name} disabled={loading}/>
          {!loading && <Typography>
            {!isMobile ? t("drag&drop") : t("selectFile")}
          </Typography>}
          <Box>
            {loading && <AppLoader loader={loading} />}

          </Box>
        </div>

        {preview && (
          <Box marginLeft={1} marginRight={1}>
            <img
              src={preview}
              alt="Preview"
              width="auto"
              height="auto"
              style={{ maxWidth: 80, maxHeight: 60, borderRadius: 2 }}
            />
          </Box>
        )}
      </Box>
      <Typography fontSize={12} paddingTop={1}>Only JPG, PNG files under 5MB are supported.</Typography>
      {error && (
        <FormHelperText sx={{ color: colors.error, fontSize: 12, marginLeft: 0 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default ImageUploader;
